import React, { useId } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import EditNoteOutlined from "@mui/icons-material/EditNoteOutlined";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import Help from "@mui/icons-material/Help";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";

/**
 * - confirm: an action that is hard to undo; the button names the action.
 * - destructive: red action, focus starts on Cancel, clicking outside does not close.
 * - prompt: one or more labeled fields (pass them as children); Enter submits.
 * - notice: something blocks the person; one button.
 */
export type ConfirmDialogVariant =
  | "confirm"
  | "destructive"
  | "prompt"
  | "notice";

/** Colour of the icon disc next to the title. 'brand' is the CSI Pay blue. */
export type ConfirmDialogTone = "brand" | "info" | "ok" | "warn" | "err";

export interface ConfirmDialogProps {
  open: boolean;
  /** Default 'confirm'. */
  variant?: ConfirmDialogVariant;
  /** Icon disc colour. Default: brand for confirm/prompt, warn for destructive/notice. */
  tone?: ConfirmDialogTone;
  /** Replaces the default icon in the disc. */
  icon?: React.ReactNode;
  title: React.ReactNode;
  /** Explanatory text under the title. A string renders as a paragraph. */
  body?: React.ReactNode;
  /** Extra content under the body: lists, choices, or the prompt's labeled fields. */
  children?: React.ReactNode;
  /** Names what happens, e.g. "Convert 2 bills" — never "OK". */
  confirmLabel: string;
  /** Default 'Cancel'. Not shown for the notice variant. */
  cancelLabel?: string;
  /** Called by the action button (and by Enter inside the fields). */
  onConfirm: () => void;
  /** Called by Cancel, Esc and (except destructive) clicking outside. */
  onClose: () => void;
  /** Shows a spinner on the action and blocks closing while true. */
  busy?: boolean;
  /** Disables the action button, e.g. until a required field is filled. */
  confirmDisabled?: boolean;
  /** Dialog width in px. Default 400. */
  width?: number;
}

const defaultTone: Record<ConfirmDialogVariant, ConfirmDialogTone> = {
  confirm: "brand",
  prompt: "brand",
  destructive: "warn",
  notice: "warn",
};

const defaultIcon = (
  variant: ConfirmDialogVariant,
  tone: ConfirmDialogTone
): React.ReactNode => {
  if (variant === "confirm") return <Help />;
  if (variant === "prompt") return <EditNoteOutlined />;
  if (variant === "destructive") return <WarningAmberOutlined />;
  switch (tone) {
    case "err":
      return <ErrorOutline />;
    case "ok":
      return <CheckCircleOutline />;
    case "warn":
      return <WarningAmberOutlined />;
    default:
      return <InfoOutlined />;
  }
};

/**
 * The shared dialog that replaces the browser's alert / confirm / prompt (AB#6872,
 * "Dialogs"): icon disc + title, explanatory body, and an action button that names
 * what happens. Esc cancels (except while busy).
 *
 *   <ConfirmDialog open={open} variant="destructive" title="Apply changes to 1,204 bills?"
 *     body="This can't be undone here." confirmLabel="Run and apply"
 *     onConfirm={apply} onClose={() => setOpen(false)} busy={saving} />
 */
export function ConfirmDialog({
  open,
  variant = "confirm",
  tone,
  icon,
  title,
  body,
  children,
  confirmLabel,
  cancelLabel = "Cancel",
  onConfirm,
  onClose,
  busy = false,
  confirmDisabled = false,
  width = 400,
}: ConfirmDialogProps) {
  const id = useId();
  const titleId = `${id}-title`;
  const bodyId = `${id}-body`;
  const formId = `${id}-form`;
  const discTone = tone ?? defaultTone[variant];
  const isDestructive = variant === "destructive";
  const isNotice = variant === "notice";
  const actionDisabled = busy || confirmDisabled;

  const handleClose = (
    _event: object,
    reason: "backdropClick" | "escapeKeyDown"
  ) => {
    if (busy) return;
    if (reason === "backdropClick" && isDestructive) return;
    onClose();
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!actionDisabled) onConfirm();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown={busy}
      maxWidth={false}
      aria-labelledby={titleId}
      aria-describedby={body ? bodyId : undefined}
      PaperProps={{
        sx: { width, maxWidth: "calc(100% - 32px)", margin: 4 },
      }}
    >
      <DialogTitle
        id={titleId}
        sx={{ display: "flex", alignItems: "center", gap: 3 }}
      >
        <Box
          aria-hidden
          sx={(theme) => {
            const colors =
              discTone === "brand"
                ? {
                    bg: theme.palette.brandTint,
                    fg: theme.palette.primary.dark,
                  }
                : theme.palette.status[discTone];
            return {
              width: 36,
              height: 36,
              borderRadius: "50%",
              flex: "none",
              display: "grid",
              placeItems: "center",
              backgroundColor: colors.bg,
              color: colors.fg,
              "& .MuiSvgIcon-root": { fontSize: 20 },
            };
          }}
        >
          {icon ?? defaultIcon(variant, discTone)}
        </Box>
        <Box component="span" sx={{ minWidth: 0 }}>
          {title}
        </Box>
      </DialogTitle>

      {(body || children) && (
        <DialogContent sx={{ display: "grid", gap: 3 }}>
          {body &&
            (typeof body === "string" ? (
              <DialogContentText id={bodyId}>{body}</DialogContentText>
            ) : (
              <Box
                id={bodyId}
                sx={{
                  fontSize: "0.84375rem",
                  lineHeight: 1.5,
                  color: "text.secondary",
                }}
              >
                {body}
              </Box>
            ))}
          {children && (
            <Box
              component="form"
              id={formId}
              noValidate
              onSubmit={handleSubmit}
              sx={{ display: "grid", gap: 3 }}
            >
              {children}
            </Box>
          )}
        </DialogContent>
      )}

      <DialogActions>
        {!isNotice && (
          <Button
            variant="outlined"
            color="inherit"
            onClick={onClose}
            disabled={busy}
            autoFocus={isDestructive}
            sx={{ borderColor: "divider", color: "text.secondary" }}
          >
            {cancelLabel}
          </Button>
        )}
        <Button
          variant="contained"
          color={isDestructive ? "error" : "primary"}
          type={children ? "submit" : "button"}
          form={children ? formId : undefined}
          onClick={children ? undefined : onConfirm}
          disabled={actionDisabled}
          autoFocus={isNotice}
          startIcon={
            busy ? <CircularProgress size={16} color="inherit" /> : undefined
          }
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ConfirmDialog;
